import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import axios from '../../../axiosConfig';

const CategoryProductPopup = ({ 
  open, 
  onClose, 
  categories, 
  onSave, 
  activeCategory,
  fetchLatestDataAndMap,
  onProductAdded 
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('categories'); // 'categories', 'subcategories', 'products'
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setViewMode('categories');
      setCurrentCategory(null);
      setCurrentSubCategory(null);
      setSelectedProducts([]);
      setAvailableProducts([]);
    }
  }, [open]);

  // Get all products from a category (including subcategories)
  const getAllProductsFromCategory = (category) => {
    let products = [...(category.cat_products || [])];
    
    // Also get products from subcategories if they exist
    if (category.sub_categories && category.sub_categories.length > 0) {
      category.sub_categories.forEach(subCat => {
        if (subCat.cat_products) {
          products = [...products, ...subCat.cat_products];
        }
      });
    }
    
    return products;
  };

  const handleCategoryClick = (category) => {
    // Check what this category contains
    const hasSubCategories = category.sub_categories && category.sub_categories.length > 0;
    const hasProducts = category.cat_products && category.cat_products.length > 0;
    
    if (hasSubCategories) {
      // Navigate to subcategories view
      setCurrentCategory(category);
      setViewMode('subcategories');
    } else if (hasProducts) {
      // Show products directly
      setCurrentCategory(category);
      setAvailableProducts(category.cat_products || []);
      setViewMode('products');
    } else {
      // Empty category - show message
      setCurrentCategory(category);
      setAvailableProducts([]);
      setViewMode('products');
    }
  };

  const handleSubCategoryClick = (subCategory) => {
    setCurrentSubCategory(subCategory);
    setAvailableProducts(subCategory.cat_products || []);
    setViewMode('products');
  };

  const handleProductToggle = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.ProductId === product.ProductId);
      if (isSelected) {
        return prev.filter(p => p.ProductId !== product.ProductId);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleBackClick = () => {
    if (viewMode === 'products' && currentSubCategory) {
      // Go back to subcategories
      setCurrentSubCategory(null);
      setViewMode('subcategories');
    } else if (viewMode === 'products' && currentCategory) {
      // Go back to categories/subcategories
      if (currentCategory.sub_categories && currentCategory.sub_categories.length > 0) {
        setViewMode('subcategories');
      } else {
        setViewMode('categories');
        setCurrentCategory(null);
      }
    } else if (viewMode === 'subcategories') {
      // Go back to categories
      setViewMode('categories');
      setCurrentCategory(null);
    }
  };

  const handleSave = async () => {
    if (selectedProducts.length === 0) {
      onClose();
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ProductId: selectedProducts.map(product => product.ProductId),
        CategoryId: activeCategory.CategoryId,
      };

      const response = await axios.put('/partners/menu_products', payload);
      
      if (response.data.success) {
        // Call fetchLatestDataAndMap to refresh the parent component
        if (fetchLatestDataAndMap) {
          await fetchLatestDataAndMap();
        }
        
        // Call onProductAdded if provided
        if (onProductAdded) {
          onProductAdded();
        }
        
        onClose();
      }
    } catch (error) {
      console.error('Error saving products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (viewMode) {
      case 'categories':
        return 'Select Source';
      case 'subcategories':
        return `Select from ${currentCategory?.name || 'Subcategories'}`;
      case 'products':
        if (currentSubCategory) {
          return `Products in ${currentSubCategory.name}`;
        } else if (currentCategory) {
          return `Products in ${currentCategory.name}`;
        }
        return 'Select Products';
      default:
        return 'Select Products';
    }
  };

  const renderCategories = () => {
    return (
      <List>
        {categories.map((category) => {
          const productCount = getAllProductsFromCategory(category).length;
          const subCategoryCount = category.sub_categories?.length || 0;
          
          return (
            <ListItem 
              key={category.CategoryId || category.id} 
              button 
              onClick={() => handleCategoryClick(category)}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <CategoryIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <ListItemText 
                    primary={category.name}
                    secondary={
                      <React.Fragment>
                        {subCategoryCount > 0 && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {subCategoryCount} subcategor{subCategoryCount === 1 ? 'y' : 'ies'}
                          </Typography>
                        )}
                        {subCategoryCount > 0 && productCount > 0 && ' • '}
                        {productCount > 0 && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {productCount} product{productCount === 1 ? '' : 's'}
                          </Typography>
                        )}
                        {subCategoryCount === 0 && productCount === 0 && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            No items
                          </Typography>
                        )}
                      </React.Fragment>
                    }
                  />
                </Box>
                <Typography variant="body2" color="primary">
                  Select →
                </Typography>
              </Box>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderSubCategories = () => {
    if (!currentCategory || !currentCategory.sub_categories) return null;
    
    return (
      <List>
        <ListItem 
          button 
          onClick={() => {
            // Show products from main category
            setAvailableProducts(currentCategory.cat_products || []);
            setViewMode('products');
          }}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 1,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <InventoryIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Box sx={{ flexGrow: 1 }}>
              <ListItemText 
                primary="Main Category Products"
                secondary={
                  <Typography component="span" variant="body2" color="text.secondary">
                    {currentCategory.cat_products?.length || 0} product{currentCategory.cat_products?.length === 1 ? '' : 's'}
                  </Typography>
                }
              />
            </Box>
            <Typography variant="body2" color="primary">
              Select →
            </Typography>
          </Box>
        </ListItem>
        
        <Divider sx={{ my: 2 }}>OR</Divider>
        
        <Typography variant="subtitle2" sx={{ px: 2, mb: 1, color: 'text.secondary' }}>
          Select from subcategories:
        </Typography>
        
        {currentCategory.sub_categories.map((subCategory) => {
          const productCount = subCategory.cat_products?.length || 0;
          
          return (
            <ListItem 
              key={subCategory.CategoryId || subCategory.id} 
              button 
              onClick={() => handleSubCategoryClick(subCategory)}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <CategoryIcon sx={{ mr: 2, color: 'secondary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <ListItemText 
                    primary={subCategory.name}
                    secondary={
                      <Typography component="span" variant="body2" color="text.secondary">
                        {productCount} product{productCount === 1 ? '' : 's'}
                      </Typography>
                    }
                  />
                </Box>
                <Typography variant="body2" color="primary">
                  Select →
                </Typography>
              </Box>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderProducts = () => {
    if (availableProducts.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No products available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This category doesn't have any products to add.
          </Typography>
          <Button 
            variant="outlined" 
            onClick={handleBackClick}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      );
    }

    return (
      <List>
        {availableProducts.map((product) => {
          const isSelected = selectedProducts.some(p => p.ProductId === product.ProductId);
          
          return (
            <ListItem 
              key={product.ProductId || product.id} 
              button 
              onClick={() => handleProductToggle(product)}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: isSelected ? '#e8f5e8' : '#f5f5f5',
                },
                backgroundColor: isSelected ? '#e8f5e8' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <InventoryIcon sx={{ mr: 2, color: isSelected ? 'success.main' : 'action.active' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <ListItemText 
                    primary={product.name}
                    secondary={
                      <Typography component="span" variant="body2" color="text.secondary">
                        Price: ${product.priceVAT || product.price || '0.00'}
                      </Typography>
                    }
                  />
                </Box>
                <Checkbox
                  edge="end"
                  checked={isSelected}
                  onChange={() => handleProductToggle(product)}
                  sx={{
                    color: 'success.main',
                    '&.Mui-checked': {
                      color: 'success.main',
                    },
                  }}
                />
              </Box>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'categories':
        return renderCategories();
      case 'subcategories':
        return renderSubCategories();
      case 'products':
        return renderProducts();
      default:
        return renderCategories();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {(viewMode === 'subcategories' || viewMode === 'products') && (
          <IconButton 
            onClick={handleBackClick} 
            sx={{ mr: 1 }}
            disabled={loading}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {getTitle()}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ minHeight: 400 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderContent()
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Box>
            {selectedProducts.length > 0 && (
              <Typography variant="body2" color="primary">
                {selectedProducts.length} product{selectedProducts.length === 1 ? '' : 's'} selected
              </Typography>
            )}
          </Box>
          <Box>
            <Button 
              onClick={onClose} 
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            {viewMode === 'products' && (
              <Button 
                onClick={handleSave} 
                color="primary" 
                variant="contained"
                disabled={loading || selectedProducts.length === 0}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  `Add ${selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}`
                )}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryProductPopup;